/*
 * KAS LUXURY — the ring size drives the price, is TYPED, and has to be given.
 *
 * WHAT CHANGED IN THIS VERSION  (19 Oct 2026)
 * The customer now TYPES their size into a box instead of scrolling a 29-item dropdown.
 * The box accepts whole and quarter sizes only -- 7, 7.25, 7.5, 7.75 -- and still carries
 * a list of every valid size, so it can be picked from as well as typed into. One control,
 * not two: asking the same question twice is the confusion this whole file exists to
 * remove, so the theme's own dropdown is hidden once the box is working.
 *
 * THE PROBLEM THIS SOLVES
 * Pricing a larger ring higher needs a variant, because only a variant carries a price.
 * But a customer should never have to understand "size bands" -- they should give their
 * size and see the right price. The "Size band" variant stays as the pricing mechanism
 * but is hidden; the typed size is mapped onto the correct band automatically. The exact
 * size travels with the order as a line item property, so production gets "US 7.25", not
 * "US 7.25 - 8.5".
 *
 * WHY THE SIZE IS COMPULSORY
 * Hiding the band left the CHEAPEST band selected by default. A customer who never gave a
 * size could buy at the US 3-5 price and then ask for a US 9: a silent undercharge of up
 * to $100 on a ring and $160 on a men's ring. The band IS the price, so the thing that
 * chooses it cannot be optional. Both ways off this page are closed until a VALID size is
 * present:
 *
 *   - Add to Bag. Blocked three times over -- the button is marked unavailable, and submit
 *     and click are both caught in the CAPTURE phase on the document, so they run before
 *     any handler the theme attached to the form or to the button.
 *   - The express checkout button. It posts straight to Shopify's checkout and runs none
 *     of this page's checks, so CSS removes it from the page until a size is given.
 *
 * Note what is NOT the fix: marking the field `required`. The theme renders the cart form
 * with `novalidate`, so browser validation is switched off and `required` would have
 * looked like a fix while changing nothing. The gate below is the only enforcement, which
 * is why it is installed three ways and why "valid" is checked rather than "non-empty".
 *
 * BLANK IS NOT THE ONLY WAY TO BE WRONG, which is the real difference from a dropdown.
 * A dropdown could only be unset; a box can hold "seven", "7.3", "0", "12" on a ring that
 * stops at 10, or a half-finished "7." mid-keystroke. So the gate tests for a size that is
 * genuinely orderable -- inside the bands this product actually sells, and on a quarter
 * step -- and says which of those two things is wrong. Anything else would let a ring be
 * ordered at a size the factory cannot make.
 *
 * RINGS ONLY. Nothing here installs unless the page has BOTH a "Size band" variant option
 * and the theme's "Ring size (US)" field. Checked against all 307 published products on
 * 19 Oct 2026: exactly 167 carry a Size band option, and those are exactly the 167 whose
 * title says ring or band -- no chain, bracelet, earring or pendant has one, and no ring
 * lacks one. So chains and pendants get no size box and no gate, and stay buyable in one
 * click. Product type was NOT used for this: it is blank on many rings (103 of 167 set it)
 * and would have left 64 rings ungated.
 *
 * THE BANDS, AND THEREFORE THE LEGAL SIZES, ARE READ FROM THE PAGE.
 * Band labels are "US 3 - 5", "US 8.75 - 10", so the bounds are parsed from them and the
 * smallest and largest give the range the box accepts. Men's rings carry six bands and
 * reach further; they get a wider box from the same file, with no code change. A label
 * that does not match that shape is skipped rather than guessed at.
 *
 * FAIL-SAFE, UNCHANGED AND LOAD-BEARING
 * The band selector is hidden, the theme's dropdown is hidden, and the gate is installed
 * ONLY once a working mapping and a working box exist. If anything is missing -- no bands,
 * no field, unparseable labels, no place to put the box -- nothing is hidden, NO gate is
 * installed, and the customer uses the theme's own controls, which still price the ring
 * correctly. A broken script must never leave a product unbuyable. That is not
 * theoretical: the first version of this file shipped with doubled backslashes, matched
 * nothing and bailed out, and because of this rule the only visible symptom was that the
 * band selector did not disappear.
 *
 * Every element is looked up BY NAME on each pass instead of being held in a variable, so
 * a re-render by the theme's variant picker can never leave this file writing to a node
 * that is no longer on the page.
 */
(function () {
  'use strict';

  var SIZE_LABEL = 'Ring size (US)';
  var BAND_OPTION = 'Size band';
  var BAND_NAME = 'options[' + BAND_OPTION + ']';
  var PROPERTY_NAME = 'properties[' + SIZE_LABEL + ']';
  var CART_FORM = 'form[action*="/cart/add"]';

  var BOX_ID = 'kas-size-box';
  var LIST_ID = 'kas-size-list';
  var NOTE_ID = 'kas-size-note';
  var ERR_ID = 'kas-size-error';
  var WRAP_ID = 'kas-size-field';

  var STEP = 0.25;

  // THIS WHOLE FILE CONTAINS NOT ONE BACKSLASH, ON PURPOSE. Do not "tidy" these classes
  // back into the usual shorthand for a digit or for whitespace.
  //
  // The file reaches the store inside a GraphQL block string, which passes backslashes
  // through verbatim, while the layers above it each hold an opinion about how many
  // backslashes to keep. That has broken this exact file twice: the first version shipped
  // a doubled backslash and matched nothing, and the second did it again from the other
  // direction. Spelling the classes out longhand removes the hazard completely instead of
  // getting the count right once -- and it also means the stored byte count can be
  // compared straight against the local file, which is how the second break was caught.
  //
  // Nothing is given up. [0-9] is precisely what the digit shorthand means in JavaScript,
  // and [.] is precisely an escaped full stop.
  var NUMBER = /([0-9]+(?:[.][0-9]+)?)/;
  var RANGE = /([0-9]+(?:[.][0-9]+)?)[ ]*[-–—][ ]*([0-9]+(?:[.][0-9]+)?)/;
  var ONLY_SIZE = /^[0-9]+(?:[.][0-9]+)?$/;

  function num(value) {
    var m = String(value).match(NUMBER);
    return m ? parseFloat(m[1]) : null;
  }

  // "US 7.25 - 8.5" -> { lo: 7.25, hi: 8.5 }
  function parseBand(label) {
    var m = String(label).match(RANGE);
    if (!m) {
      return null;
    }
    var lo = parseFloat(m[1]);
    var hi = parseFloat(m[2]);
    return hi >= lo ? { lo: lo, hi: hi } : null;
  }

  // Generous on the way in, strict on the way out. People type "US 7", "size 7.5",
  // "7,5" with a comma decimal, or leave a trailing space. All of those are a clear
  // intention and are accepted; what is NOT accepted is a size the factory cannot make,
  // and that is decided by validate() below, not here.
  function parseTyped(raw) {
    var s = String(raw == null ? '' : raw).toLowerCase();
    s = s.replace(/us/g, ' ').replace(/size/g, ' ').replace(/,/g, '.');
    s = s.replace(/[^0-9. ]/g, ' ').replace(/[ ]+/g, ' ');
    s = s.replace(/^[ ]+/, '').replace(/[ ]+$/, '');
    if (!ONLY_SIZE.test(s)) {
      return null;
    }
    var n = parseFloat(s);
    return isFinite(n) ? n : null;
  }

  function onStep(size) {
    var k = size / STEP;
    return Math.abs(k - Math.round(k)) < 0.000001;
  }

  function tidy(size) {
    // 7 -> "7", 7.5 -> "7.5", 7.25 -> "7.25". No trailing zeros on a ring size.
    return String(Math.round(size * 100) / 100);
  }

  function findByName(name) {
    var all = document.querySelectorAll('select, input');
    for (var i = 0; i < all.length; i++) {
      if (all[i].name === name) {
        return all[i];
      }
    }
    return null;
  }

  function box() {
    return document.getElementById(BOX_ID);
  }

  function optionRow(el) {
    return el.closest('.product__option') ||
           el.closest('[data-product-option]') ||
           el.parentElement;
  }

  function fieldAnchor(el) {
    return el.closest('.product__custom-option') || optionRow(el);
  }

  function addButton() {
    return document.querySelector(CART_FORM + ' [data-add-to-cart]') ||
           document.querySelector(CART_FORM + ' [name="add"]');
  }

  function start() {
    var bandSelect = findByName(BAND_NAME);
    var sizeField = findByName(SIZE_LABEL) || findByName(PROPERTY_NAME);

    // Not a ring page. Install nothing, so chains, bracelets, earrings and pendants are
    // untouched and remain one-click buyable.
    if (!bandSelect || !sizeField) {
      return;
    }

    var bands = [];
    for (var i = 0; i < bandSelect.options.length; i++) {
      var range = parseBand(bandSelect.options[i].value);
      if (range) {
        bands.push({ value: bandSelect.options[i].value, lo: range.lo, hi: range.hi });
      }
    }
    if (!bands.length) {
      return;
    }
    bands.sort(function (a, b) { return a.lo - b.lo; });

    var MIN = bands[0].lo;
    var MAX = bands[bands.length - 1].hi;

    function bandFor(size) {
      for (var i = 0; i < bands.length; i++) {
        if (size >= bands[i].lo && size <= bands[i].hi) {
          return bands[i].value;
        }
      }
      // Outside every band: clamp, so a size can never be unbuyable.
      return size < MIN ? bands[0].value : bands[bands.length - 1].value;
    }

    // -> { size: number|null, why: ''|'empty'|'range'|'step' }
    // The reason is carried so the message can name the one thing that is wrong.
    function validate(raw) {
      var s = String(raw == null ? '' : raw);
      if (!s.replace(/[ ]/g, '').length) {
        return { size: null, why: 'empty' };
      }
      var n = parseTyped(s);
      if (n === null) {
        // Something is in the box but it is not a size that can be read: "seven", "7-8",
        // or a half-finished "7." mid-keystroke. "7." stays BLOCKED rather than being
        // read as 7, so the gate does not flicker open and shut while someone types
        // 7.5 one character at a time.
        return { size: null, why: 'format' };
      }
      if (n < MIN || n > MAX) {
        return { size: null, why: 'range' };
      }
      if (!onStep(n)) {
        return { size: null, why: 'step' };
      }
      return { size: n, why: '' };
    }

    function current() {
      var b = box();
      if (b) {
        return validate(b.value);
      }
      // No box was built: fall back to whatever the theme's own field holds, so the gate
      // still protects the price rather than switching itself off.
      var f = findByName(SIZE_LABEL);
      var n = f ? num(f.value) : null;
      return { size: n, why: n === null ? 'empty' : '' };
    }

    function sizeNow() {
      return current().size;
    }

    // ---------------------------------------------------------------- the field
    function buildField() {
      if (box()) {
        return true;
      }
      var f = findByName(SIZE_LABEL) || findByName(PROPERTY_NAME);
      if (!f) {
        return false;
      }
      var anchor = fieldAnchor(f);
      if (!anchor || !anchor.parentNode) {
        return false;
      }

      var wrap = document.createElement('div');
      wrap.id = WRAP_ID;
      wrap.className = 'kas-size-field';

      var label = document.createElement('label');
      label.className = 'kas-size-label';
      label.setAttribute('for', BOX_ID);
      label.textContent = 'Your ring size (US)';

      var input = document.createElement('input');
      input.id = BOX_ID;
      input.className = 'kas-size-input';
      input.type = 'text';
      input.setAttribute('inputmode', 'decimal');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('placeholder', 'e.g. 7, 7.25, 7.5, 7.75');
      input.setAttribute('aria-describedby', NOTE_ID);
      input.setAttribute('list', LIST_ID);

      // Every size the factory will make, so the box can be picked from as well as typed
      // into. Built from the bands on the page, so a men's ring offers its wider range
      // from the same code.
      var list = document.createElement('datalist');
      list.id = LIST_ID;
      for (var s = MIN; s <= MAX + 0.0001; s += STEP) {
        var o = document.createElement('option');
        o.value = tidy(s);
        list.appendChild(o);
      }

      var note = document.createElement('p');
      note.id = NOTE_ID;
      note.className = 'kas-size-note';
      note.textContent = 'Whole and quarter sizes, ' + tidy(MIN) + ' to ' + tidy(MAX) +
                         ' — for example 7, 7.25, 7.5 or 7.75. A larger ring uses more ' +
                         'gold, so the price changes with the size.';

      var err = document.createElement('p');
      err.id = ERR_ID;
      err.className = 'kas-size-error';
      err.setAttribute('role', 'status');

      wrap.appendChild(label);
      wrap.appendChild(input);
      wrap.appendChild(list);
      wrap.appendChild(note);
      wrap.appendChild(err);
      anchor.parentNode.insertBefore(wrap, anchor);

      // The theme's own control is hidden only now that ours is on the page and working.
      anchor.setAttribute('data-kas-hidden-size', 'true');

      input.addEventListener('input', apply);
      input.addEventListener('change', apply);
      input.addEventListener('blur', function () {
        var v = validate(input.value);
        if (v.size !== null) {
          input.value = tidy(v.size);   // "US 7,50" -> "7.5", once they have finished
        }
      });
      return true;
    }

    function message(why) {
      if (why === 'range') {
        return 'This ring is made from US ' + tidy(MIN) + ' to US ' + tidy(MAX) +
               '. Tell us the size you need and we will quote it.';
      }
      if (why === 'step') {
        return 'Use a whole or quarter size — 7, 7.25, 7.5 or 7.75, not 7.3.';
      }
      if (why === 'format') {
        return 'Enter the size as a number — for example 7, 7.25, 7.5 or 7.75.';
      }
      return 'Enter your ring size to continue.';
    }

    // Marks the page blocked or clear. The attribute goes on <html> as well as on the
    // form, because the price sits outside the form and needs the same state.
    function refresh() {
      var state = current();
      var ok = state.size !== null;
      var flag = ok ? 'ok' : 'blocked';

      document.documentElement.setAttribute('data-kas-size-gate', flag);
      var forms = document.querySelectorAll(CART_FORM);
      for (var i = 0; i < forms.length; i++) {
        forms[i].setAttribute('data-kas-size-gate', flag);
      }

      var b = box();
      if (b) {
        b.setAttribute('aria-invalid', ok ? 'false' : 'true');
      }
      var err = document.getElementById(ERR_ID);
      if (err) {
        // Nothing is said while the box is still empty and untouched -- the guidance line
        // already says what to do, and shouting before they have typed is not help.
        var touched = b && String(b.value).replace(/[ ]/g, '').length > 0;
        var show = !ok && touched;
        err.textContent = show ? message(state.why) : '';
        err.setAttribute('data-kas-show', show ? 'true' : 'false');
      }

      // THE BUTTON IS MARKED UNAVAILABLE BUT IS NOT `disabled`, AND THAT IS DELIBERATE.
      //
      // A truly disabled button dispatches no click event at all -- not on itself, not to
      // any ancestor. So the capture-phase handler below never runs, and a customer who
      // taps the greyed button is told nothing whatsoever. That dead tap was found by the
      // harness: the submit was correctly blocked and the explanation never appeared.
      //
      // aria-disabled tells assistive technology the same thing `disabled` would, the CSS
      // gives it the gated look and swaps its label to "ENTER YOUR RING SIZE", and the
      // click now reaches us so the reason can be said out loud. Nothing is given up on
      // the enforcement side: submit and click are both still stopped in the capture
      // phase, before any handler the theme attached, and the express checkout button is
      // removed from the page entirely while blocked.
      //
      // A button the THEME disabled is never touched -- that means genuinely unavailable,
      // and is not ours to override. Every ring variant is CONTINUE, so in practice the
      // theme never disables one.
      var btn = addButton();
      if (btn && !(btn.disabled && btn.getAttribute('data-kas-gated') !== '1')) {
        if (!ok) {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-kas-gated', '1');
        } else {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-kas-gated');
        }
      }

      // The size written on the order must be the size that was typed. Both the theme's
      // visible field and the hidden in-form property are kept in step, so whichever one
      // Shopify reads, it reads the same number.
      if (ok) {
        var text = 'US ' + tidy(state.size);
        var vis = findByName(SIZE_LABEL);
        var prop = findByName(PROPERTY_NAME);
        [vis, prop].forEach(function (f) {
          if (!f) {
            return;
          }
          if (f.tagName === 'SELECT') {
            for (var j = 0; j < f.options.length; j++) {
              if (num(f.options[j].value) === state.size) {
                if (f.value !== f.options[j].value) {
                  f.value = f.options[j].value;
                }
                return;
              }
            }
            return;   // no matching option: leave it rather than write a size it denies
          }
          if (f.value !== text) {
            f.value = text;
          }
        });
      }

      return ok;
    }

    function apply() {
      var size = sizeNow();
      if (size === null) {
        refresh();
        return;
      }
      var band = findByName(BAND_NAME);
      if (band) {
        var wanted = bandFor(size);
        if (band.value !== wanted) {
          band.value = wanted;
          band.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      refresh();
    }

    function isCartForm(el) {
      return !!(el && el.getAttribute &&
                String(el.getAttribute('action') || '').indexOf('/cart/add') > -1);
    }

    function stop(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var b = box();
      if (b && !String(b.value).replace(/[ ]/g, '').length) {
        b.value = '';            // so refresh() treats it as touched and speaks up
        b.setAttribute('data-kas-touched', '1');
      }
      refresh();
      var err = document.getElementById(ERR_ID);
      if (err) {
        err.textContent = message(current().why);
        err.setAttribute('data-kas-show', 'true');
      }
      var target = b || findByName(SIZE_LABEL);
      if (!target) {
        return;
      }
      if (target.scrollIntoView) {
        target.scrollIntoView({ block: 'center' });
      }
      try {
        target.focus();
      } catch (err2) {
        // Focus is a courtesy. It must never be the reason the block fails to hold.
      }
    }

    // Capture phase, on the document, so these run before any handler the theme put on
    // the form or the button, and so a cart form added to the page later is covered too.
    document.addEventListener('submit', function (e) {
      if (sizeNow() !== null) {
        return;
      }
      if (!isCartForm(e.target)) {
        return;
      }
      stop(e);
    }, true);

    document.addEventListener('click', function (e) {
      if (sizeNow() !== null) {
        return;
      }
      var btn = e.target && e.target.closest ?
                e.target.closest('[data-add-to-cart], [name="add"]') : null;
      if (!btn || !btn.closest(CART_FORM)) {
        return;
      }
      stop(e);
    }, true);

    // Any change in the buy area re-asserts the band and the gate. Picking a metal AFTER
    // a size must never drop the price back to the cheapest band.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'SELECT') {
        return;
      }
      if (t.name === BAND_NAME) {
        refresh();   // our own write landing. Do not recurse.
        return;
      }
      apply();
    }, true);

    var built = buildField();
    apply();
    // The theme can re-run its variant picker after DOMContentLoaded, and can replace the
    // whole buy block, taking our field with it. Rebuild if it has gone.
    window.addEventListener('load', function () {
      buildField();
      apply();
    });

    // The band selector is pricing plumbing. Hide it only now, with a working mapping.
    var row = optionRow(bandSelect);
    if (row) {
      row.setAttribute('data-kas-hidden-band', 'true');
    }
    if (!built) {
      // No box: the theme's own dropdown stays visible and usable, and the gate above is
      // still live against it. Worse UX, still correct money, still buyable.
      document.documentElement.setAttribute('data-kas-size-box', 'absent');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
